import { createFileRoute, Link } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { useQuery } from "@tanstack/react-query";
import { Calendar, ArrowLeft } from "lucide-react";
import { buildBreadcrumbJsonLd } from "@/lib/breadcrumb-jsonld";
import { buildSeoMeta, clampDescription } from "@/lib/seo";
import { buildArticleJsonLd } from "@/lib/jsonld";

const fetchPostMeta = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => {
    const v = (input as { slug?: string })?.slug;
    if (typeof v !== "string" || !v) throw new Error("slug required");
    return { slug: v };
  })
  .handler(async ({ data }) => {
    const { data: row } = await supabaseAdmin
      .from("blog_posts")
      .select("slug, title, excerpt, seo_title, seo_description, featured_image, published_at, updated_at, status")
      .eq("slug", data.slug)
      .eq("status", "published")
      .maybeSingle();
    return row;
  });

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params }) => {
    try {
      const post = await fetchPostMeta({ data: { slug: params.slug } });
      return { post };
    } catch {
      return { post: null };
    }
  },
  head: ({ loaderData, params }) => {
    const post = loaderData?.post;
    if (!post) {
      return buildSeoMeta({
        title: "Artikel ikke fundet · AutoXChange",
        description: "Denne artikel findes ikke længere — se vores andre indlæg.",
        path: `/blog/${params.slug}`,
        noIndex: true,
      });
    }
    const title = post.seo_title || `${post.title} · AutoXChange`;
    const description = post.seo_description || clampDescription(post.excerpt) ||
      "Læs guide og indsigt fra AutoXChange.";
    const seo = buildSeoMeta({
      title,
      description,
      path: `/blog/${post.slug}`,
      image: post.featured_image || undefined,
      // Beskrivende alt-tekst pr. artikel — bruges som og:image:alt /
      // twitter:image:alt for tilgængelighed (skærmlæsere) og bedre
      // social-preview/SEO-signal end et generisk fallback.
      imageAlt: post.featured_image
        ? `Forsidebillede: ${post.title} — AutoXChange blog`
        : undefined,
      ogType: "article",
      publishedTime: post.published_at || undefined,
      modifiedTime: post.updated_at || undefined,
    });
    return {
      ...seo,
      scripts: [
        {
          type: "application/ld+json",
          children: buildArticleJsonLd({
            slug: post.slug,
            title: post.title,
            description: post.excerpt,
            image: post.featured_image,
            publishedAt: post.published_at,
            updatedAt: post.updated_at,
          }),
        },
      ],
    };
  },
  component: BlogPost,
});

function BlogPost() {
  const { slug } = Route.useParams();

  const { data: post, isLoading } = useQuery({
    queryKey: ["blog-post", slug],
    queryFn: async () => {
      const { data } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("slug", slug)
        .single();
      return data;
    },
  });

  if (isLoading) {
    return (
      <div className="pt-24">
        <div className="mx-auto max-w-3xl px-6 py-12">
          <div className="h-8 w-2/3 animate-pulse rounded bg-muted" />
          <div className="mt-8 space-y-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-4 animate-pulse rounded bg-muted" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="flex min-h-screen items-center justify-center pt-24">
        <div className="text-center">
          <h1 className="text-2xl font-bold">Artiklen blev ikke fundet</h1>
          <Link to="/blog" className="mt-4 inline-flex items-center gap-2 text-accent hover:underline">
            <ArrowLeft size={16} /> Tilbage til blog
          </Link>
        </div>
      </div>
    );
  }

  const breadcrumbJsonLd = buildBreadcrumbJsonLd([
    { name: "Forside", path: "" },
    { name: "Blog", path: "blog" },
    { name: post.title, path: `blog/${post.slug}` },
  ]);

  return (
    <div className="pt-20">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: breadcrumbJsonLd }} />
      <article className="mx-auto max-w-3xl px-6 py-12">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-accent mb-8">
          <ArrowLeft size={14} /> Tilbage til blog
        </Link>

        {post.featured_image && (
          <div className="mb-8 aspect-[16/9] overflow-hidden rounded-xl">
            <img src={post.featured_image} alt={post.title} className="h-full w-full object-cover" />
          </div>
        )}

        {post.tags && post.tags.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-2">
            {post.tags.map((tag) => (
              <span key={tag} className="rounded-full bg-accent/10 px-3 py-1 text-xs font-medium text-accent">{tag}</span>
            ))}
          </div>
        )}

        <h1 className="text-3xl font-bold text-foreground md:text-4xl">{post.title}</h1>

        {post.published_at && (
          <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar size={14} />
            {new Date(post.published_at).toLocaleDateString("da-DK", { year: "numeric", month: "long", day: "numeric" })}
          </div>
        )}

        <div className="mt-10 whitespace-pre-wrap text-foreground/80 leading-relaxed">{post.content}</div>
      </article>
    </div>
  );
}
